import { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Grid, GridColumn as Column, GridToolbar, GridItemChangeEvent, GridCustomCellProps } from '@progress/kendo-react-grid';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { Button } from '@progress/kendo-react-buttons';
import { fetchApi } from 'services/api';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { parsePhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';

enum OwnerNotificationAlertType {
  TurndownSMS,
  MessageSMS,
  MessageEmail,
}

type OwnerNotificationViewModel = {
  OwnerNotificationID: number;
  OwnerID: number;
  AlertType: OwnerNotificationAlertType;
  EmailAddress: string;
  PhoneNumber: string;
  DisplayName: string;
  IsActive: boolean;
  IsSmsAlert: boolean;
  IsEmailAlert: boolean;
}

type AlertTypeViewModel = {
  ID: number;
  Name: string;
  IsSmsAlert: boolean;
  IsEmailAlert: boolean;
}

type Response = {
  AlertTypes: AlertTypeViewModel[];
  OwnerNotifications: OwnerNotificationViewModel[];
}

type MyCommandCellProps = GridCustomCellProps & {
    dataItem: OwnerNotificationViewModel;
    isInEdit: boolean;
    edit: (dataItem: OwnerNotificationViewModel) => void;
    update: (dataItem: OwnerNotificationViewModel) => void;
    remove: (dataItem: OwnerNotificationViewModel) => void;
    discard: (dataItem: OwnerNotificationViewModel) => void;
    cancel: (dataItem: OwnerNotificationViewModel) => void;
};

export const MyCommandCell = (props: MyCommandCellProps) => {
  const { dataItem } = props;
  const inEdit = props.isInEdit;
  const isNewItem = dataItem.OwnerNotificationID === 0;
  const [visible, setVisible] = useState(false);
  const onDeleteData = () => {
    props.remove(props.dataItem);
    setVisible(!visible);
  };
  const toggleDialog = () => {
    setVisible(!visible);
  };

  return (
    <td {...props.tdProps} className={[props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
      <Button
        themeColor={'primary'}
        onClick={() =>
          inEdit ? (isNewItem ? props.update(dataItem) : props.update(dataItem)) : props.edit(dataItem)
        }
      >
        {inEdit ? (isNewItem ? 'Add' : 'Update') : 'Edit'}
      </Button>
      {(inEdit || dataItem.OwnerNotificationID >= 0) && <Button
        themeColor={'primary'}
        onClick={() =>
          inEdit ? (isNewItem ? props.discard(dataItem) : props.cancel(dataItem)) : toggleDialog()
        }
      >
        {inEdit ? (isNewItem ? 'Discard' : 'Cancel') : 'Remove'}
      </Button>}
      {visible && (
        <Dialog title={'Delete Data'} onClose={toggleDialog} width={350}>
          <div>Are you sure you want to delete?</div>
          <DialogActionsBar>
            <Button onClick={toggleDialog}>Cancel</Button>
            <Button themeColor='error' onClick={onDeleteData}>Delete</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </td>
  );
};

const DisplayNameCell = (props: GridCustomCellProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: props.dataIndex,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e,
        value: e.target.value
      });
    }
  };
  if (props.isInEdit) {
    return <td {...props.tdProps}>
      <input
        required
        disabled={props.dataItem.OwnerNotificationID < 0}
        type="text"
        placeholder='Contact Name'
        className="form-control"
        value={props.dataItem.DisplayName}
        onChange={handleChange}
      />
    </td>;
  }
  return <td {...props.tdProps}>{props.dataItem.DisplayName}</td>;
}

type ActiveCellProps = {
  alertTypes: AlertTypeViewModel[];
} & GridCustomCellProps;

const AlertTypeCell = (props: ActiveCellProps) => {
  const handleChange = (e: DropDownListChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: props.dataIndex,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.ID
      });
    }
  };
  if (props.isInEdit) {
    return <td {...props.tdProps}>
      <DropDownList
        disabled={props.dataItem.OwnerNotificationID < 0}
        data={props.alertTypes}
        textField="Name"
        dataItemKey="ID"
        value={props.alertTypes.find((alert) => alert.ID === props.dataItem.AlertType)}
        onChange={handleChange}
      />
    </td>;
  }
  return <td {...props.tdProps}>{props.alertTypes.find((alert) => alert.ID === props.dataItem.AlertType)?.Name}</td>;
}

const RecipientCell = (props: GridCustomCellProps) => {
  const handleChange = (value: string) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: props.dataIndex,
        dataItem: props.dataItem,
        field: props.dataItem.IsSmsAlert ? "PhoneNumber" : "EmailAddress",
        syntheticEvent: undefined,
        value: value
      });
    }
  };
  if (props.isInEdit && props.dataItem.IsSmsAlert) {
    return <td {...props.tdProps}>
      <PhoneInput
        required
        disabled={props.dataItem.OwnerNotificationID < 0}
        className="form-control"
        placeholder="Use + for international numbers"
        value={props.dataItem.PhoneNumber}
        defaultCountry="US"
        onChange={(value) => handleChange(value)}
        />
    </td>;
  }
  if (props.isInEdit && props.dataItem.IsEmailAlert) {
    return <td {...props.tdProps}>
      <input
        required
        disabled={props.dataItem.OwnerNotificationID < 0}
        type="email"
        placeholder="Email Address"
        className="form-control"
        value={props.dataItem.EmailAddress}
        onChange={(e) => handleChange(e.target.value)}
      />
    </td>;
  }
  if (props.dataItem.IsSmsAlert) {
    const value = parsePhoneNumber(props.dataItem.PhoneNumber, "US");
    return <td {...props.tdProps}>{value ? value.countryCallingCode == '1' ? value.formatNational() : value.formatInternational() : props.dataItem.PhoneNumber}</td>;
  }
  if (props.dataItem.IsEmailAlert) {
    return <td {...props.tdProps}>{props.dataItem.EmailAddress}</td>;
  }
  return <td {...props.tdProps} />
};

const IsActiveCell = (props: GridCustomCellProps) => {
  const handleChange = (e: CheckboxChangeEvent) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: props.dataIndex,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.value
      });
    }
  };
  if (props.isInEdit) {
    return <td {...props.tdProps}><Checkbox type="checkbox" disabled={props.dataItem.OwnerNotificationID == 0} checked={props.dataItem.IsActive} onChange={handleChange} /></td>;
  }
  return <td {...props.tdProps}>{props.dataItem.IsActive ? 'Yes' : 'No'}</td>;
};

const editField = 'inEdit';

const CommandCell = (props: any) => (
    <MyCommandCell {...props} editField={editField} />
);

type OwnerNotificationsManagerProps = {
  OwnerID: number;
  CloseDialog: () => void;
}

const OwnerNotificationsManager = (props: OwnerNotificationsManagerProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OwnerNotificationViewModel[]>([]);
  const [edit, setEdit] = useState<EditDescriptor>({});
  const [alertTypes, setAlertTypes] = useState<AlertTypeViewModel[]>([]);

  const getOwnerNotifications = useCallback(() => {
    return fetchApi(`/api/Asset/OwnerNotifications/${props.OwnerID}`)
      .then((response: Response): OwnerNotificationViewModel[] => {
        setAlertTypes(response.AlertTypes);
        return response.OwnerNotifications;
      })
      .catch((): OwnerNotificationViewModel[] => {
        return [];
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props.OwnerID]);

  const deleteOwnerNotification = (dataItem: OwnerNotificationViewModel) => {
    return fetchApi(`/api/Asset/OwnerNotification/${dataItem.OwnerNotificationID}`, {}, 'DELETE');
  };

  const updateOwnerNotification = (dataItem: OwnerNotificationViewModel) => {
    return fetchApi(`/api/Asset/OwnerNotification/${props.OwnerID}`, dataItem, 'POST');
  };

  useEffect(() => {
    getOwnerNotifications().then(setData);
  }, [getOwnerNotifications]);

  const remove = (dataItem: OwnerNotificationViewModel) => {
    deleteOwnerNotification(dataItem)
      .then(() => {
        getOwnerNotifications().then(setData);
      })
      .catch(() => {
        alert('Unable to delete notification!');
      });
  };
  const update = (dataItem: OwnerNotificationViewModel) => {
    updateOwnerNotification(dataItem)
      .then(() => {
        getOwnerNotifications().then(setData);
        setEdit((edit) => ({ ...edit, [dataItem.OwnerNotificationID!]: false }));
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Unable to update notification!');
      });
  };
  const discard = () => {
    const newData = [...data];
    newData.splice(0, 1);
    setData(newData);
  };
  const cancel = (dataItem: OwnerNotificationViewModel) => {
    getOwnerNotifications()
      .then((originalItems) => {
        const originalItem = originalItems.find((p) => p.OwnerNotificationID === dataItem.OwnerNotificationID);
        const isNewlyAdded = dataItem.OwnerNotificationID === 0;
        if (isNewlyAdded) {
          setData(data.filter((item) => item.OwnerNotificationID !== 0));
        } else if (originalItem) {
          setData(data.map((item) => (item.OwnerNotificationID === originalItem.OwnerNotificationID ? originalItem : item)));
          setEdit((edit) => ({ ...edit, [dataItem.OwnerNotificationID!]: false }));
        }
      });
  };
  const enterEdit = (dataItem: OwnerNotificationViewModel) => {
    setEdit((edit) => ({ ...edit, [dataItem.OwnerNotificationID!]: true }));
  };
  const itemChange = (event: GridItemChangeEvent) => {
    const alertType = event.field === 'AlertType'
      ? alertTypes.find((alert) => alert.ID === event.value)
      : undefined;

    setData(data.map((item) => {
      if (item.OwnerNotificationID !== event.dataItem.OwnerNotificationID) return item;

      return {
        ...item,
        [event.field || '']: event.value,
        ...(alertType && {
          IsSmsAlert: alertType.IsSmsAlert,
          IsEmailAlert: alertType.IsEmailAlert
        })
      };
    }));
  };
  const addNew = () => {
    const newDataItem: OwnerNotificationViewModel = {
      OwnerNotificationID: 0,
      OwnerID: 0,
      AlertType: 1,
      EmailAddress: '',
      PhoneNumber: '',
      DisplayName: '',
      IsActive: true,
      IsSmsAlert: true,
      IsEmailAlert: false
    };
    setData([newDataItem, ...data]);
    setEdit((edit) => ({ ...edit, [newDataItem.OwnerNotificationID!]: true }));
  };
  const commandCellProps = {
    edit: enterEdit,
    remove,
    discard,
    update,
    cancel,
    editField
  };
  const OwnerAlertTypeCell = useCallback((cellProps: GridCustomCellProps) => (
    <AlertTypeCell {...cellProps} alertTypes={alertTypes} />
  ), [alertTypes]);

  return <Dialog title="Owner Notifications" onClose={props.CloseDialog} className='dialog-w1140'>
    <Grid
      data={data}
      onItemChange={itemChange}
      dataItemKey='OwnerNotificationID'
      edit={edit}
      editable={true}
      showLoader={loading}
    >
      <GridToolbar>
        <Button title="Add new" themeColor={'primary'} onClick={addNew} type="button">
          Add new
        </Button>
      </GridToolbar>
      <Column field="DisplayName" title="Name" editor="text" cells={{ data: DisplayNameCell }} />
      <Column field="AlertType" title="Alert Type" cells={{ data: OwnerAlertTypeCell }} />
      <Column field="" title="Recipient" editor="text" cells={{ data: RecipientCell }} />
      <Column field="IsActive" title="Enabled" editor="boolean" cells={{ data: IsActiveCell }} />
      <Column cells={{ data: (props) => <CommandCell {...props} {...commandCellProps} /> }} />
    </Grid>
  </Dialog>;
};

export default OwnerNotificationsManager;
